import { useQuery } from '@tanstack/react-query';
import {
  fetchStudentVocabularySummary,
  fetchVocabularyJourney,
  type VocabularyJourney,
} from '../../learning/api/vocabulary';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useStudentMe } from '../../roster/hooks/useStudentMe';
import { useTutorTenantLabels } from '../../tutor/hooks/useTutorTenantLabels';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { vocabularyJourneyQueryKey } from '../queryKeys';
import {
  isDashboardIdentityReady,
  resolveDashboardStudentGrade,
} from '../utils/dashboardQueryGating';

export const VOCABULARY_JOURNEY_QUERY_KEY = ['student-dashboard', 'vocabulary-journey'] as const;

const EMPTY_JOURNEY: VocabularyJourney = {
  words_learned: 0,
  words_mastered: 0,
  attempted_words: 0,
  vocabulary_score: 0,
  practice_streak: 0,
  mastery_percentage: 0,
  recently_learned: [],
  total_assigned_words: 0,
  continue_practice: null,
};

export async function loadVocabularyJourneyData(grade: number): Promise<{
  journey: VocabularyJourney;
  totalStars: number;
}> {
  try {
    const [journey, summary] = await Promise.all([
      fetchVocabularyJourney(grade),
      fetchStudentVocabularySummary(grade).catch(() => null),
    ]);
    return {
      journey,
      totalStars: summary?.revision_stars_earned ?? 0,
    };
  } catch {
    const summary = await fetchStudentVocabularySummary(grade);
    const total = summary.total_published_words || 0;
    const mastered = summary.mastered || 0;
    return {
      journey: {
        words_learned: summary.words_learned || 0,
        words_mastered: mastered,
        attempted_words: summary.words_learned || 0,
        vocabulary_score: total > 0 ? Math.round((mastered / total) * 100) : 0,
        practice_streak: 0,
        mastery_percentage: Math.round(summary.coverage_pct || 0),
        recently_learned: [],
        total_assigned_words: total,
        continue_practice: null,
      },
      totalStars: summary.revision_stars_earned ?? 0,
    };
  }
}

/** Canonical owner for vocabulary journey + summary on the student dashboard. */
export function useVocabularyJourneyData() {
  const { isSchoolFeatureEnabled, loading: featureLoading } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { grade, isRosterLoading } = useStudentMe();
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const studentGrade = resolveDashboardStudentGrade(grade);
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);

  const enabled =
    isSchoolFeatureEnabled('vocabulary') &&
    !featureLoading &&
    !labelsLoading &&
    !labels.isMalay &&
    !isRosterLoading &&
    studentGrade !== undefined &&
    identityReady;

  const queryKey =
    studentGrade !== undefined && identityReady
      ? vocabularyJourneyQueryKey({ membershipKey, studentKey, grade: studentGrade })
      : [...VOCABULARY_JOURNEY_QUERY_KEY, 'disabled'];

  const query = useQuery({
    queryKey,
    queryFn: () => loadVocabularyJourneyData(studentGrade!),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    enabled: isSchoolFeatureEnabled('vocabulary'),
    studentGrade,
    journey: query.data?.journey ?? EMPTY_JOURNEY,
    totalStars: query.data?.totalStars ?? 0,
    loading: enabled && (query.isLoading || featureLoading || isRosterLoading),
    gradeError:
      isSchoolFeatureEnabled('vocabulary') &&
      !isRosterLoading &&
      !featureLoading &&
      studentGrade === undefined
        ? 'Your grade is not set. Ask your teacher to assign a grade.'
        : null,
    refetch: query.refetch,
  };
}
